import React from 'react';
import PropTypes from 'prop-types';
import { DateRangePicker } from 'react-dates';
import styled from 'styled-components';
import BorderSelect from '../border-select/border-select-component';

/**
 * Returns an array of hours.
 * @returns {Array} The array of hours.
 */
const getHours = () => {
  const retVal = [];
  const x = 60; // minutes interval
  let tt = 0; // start time
  const ap = ['AM', 'PM']; // AM-PM

  // loop to increment the time and push results in array
  for (let i = 0; tt < 24 * 60; i += 1) {
    const hh = Math.floor(tt / 60); // getting hours of day in 0-24 format
    const mm = tt % 60; // getting minutes of the hour in 0-59 format
    const hour = (hh % 12).toString().padStart(2, '0');
    const minute = mm.toString().padStart(2, '0');
    const period = ap[Math.floor(hh / 12)]; // AM or PM
    retVal[i] = {
      text: `${hour}:${minute} ${period}`,
      value: `${i}`,
    }; // pushing data in array in [00:00 - 12:00 AM/PM format]
    tt += x;
  }

  return retVal;
};

const DateHolder = styled.div`
  .DateInput {
    width: 195px;
  }
`;

const dateStyle = {
  width: 'calc(50% - 5px)',
  display: 'inline-block',
  marginRight: '5px',
};

/**
 * DateTimeRangePicker component.
 * @param {object} props - The component props.
 * @param {object} props.startDate - The start date.
 * @param {object} props.endDate - The end date.
 * @param {string} props.startDateId - The start date id.
 * @param {string} props.endDateId - The end date id.
 * @param {Function} props.handleFocusChange - The handleFocusChange function.
 * @param {Function} props.handleDatesChange - The handleDatesChange function.
 * @param {Function} props.handleTimeChange - The handleTimeChange function.
 * @param {string} props.focusedInput - The focusedInput.
 * @returns {JSX.Element} The rendered DateTimeRangePicker component.
 */
const DateTimeRangePicker = ({
  startDate,
  endDate,
  startDateId,
  endDateId,
  handleFocusChange,
  handleDatesChange,
  focusedInput,
  handleTimeChange,
}) => {
  /**
   * Handles the select change.
   * @param {object} e - The event object.
   * @param {object} sDate - The start date.
   * @param {object} eDate - The end date.
   * @param {string} type - The type.
   * @returns {void}
   */
  const handleSelectChange = (e, sDate, eDate, type) => {
    if (type === 'endDate') {
      eDate.utcOffset(0).set({
        hour: e.target.value * 1,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      handleTimeChange(eDate, type);
    } else {
      sDate.utcOffset(0).set({
        hour: e.target.value * 1,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      handleTimeChange(sDate, type);
    }
  };
  return (
    <DateHolder>
      <DateRangePicker
        startDate={startDate} // momentPropTypes.momentObj or null,
        startDateId={startDateId} // PropTypes.string.isRequired,
        endDate={endDate} // momentPropTypes.momentObj or null,
        endDateId={endDateId} // PropTypes.string.isRequired,
        onFocusChange={handleFocusChange}
        onDatesChange={handleDatesChange}
        focusedInput={focusedInput}
        block
        minimumNights={0}
        numberOfMonths={2}
        displayFormat="YYYY/MM/DD"
        orientation="horizontal"
        anchorDirection="left"
        isOutsideRange={() => false}
        withPortal
      />
      {startDate && (
        <div style={dateStyle}>
          <BorderSelect
            header=""
            name="status"
            label="Start Time"
            value={startDate.format('H')}
            onChange={(e) => {
              handleSelectChange(e, startDate, endDate, 'startDate');
            }}
            options={getHours()}
            stateValue
          />
        </div>
      )}
      {endDate && (
        <div style={dateStyle}>
          <BorderSelect
            header=""
            name="status"
            label="End Time"
            value={endDate.format('H')}
            onChange={(e) => {
              handleSelectChange(e, startDate, endDate, 'endDate');
            }}
            options={getHours()}
            stateValue
          />
        </div>
      )}
    </DateHolder>
  );
};

DateTimeRangePicker.defaultProps = {
  startDate: null,
  endDate: null,
  focusedInput: null,
};

DateTimeRangePicker.propTypes = {
  // Select field header
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  startDateId: PropTypes.string.isRequired,
  endDateId: PropTypes.string.isRequired,
  handleFocusChange: PropTypes.func.isRequired,
  handleDatesChange: PropTypes.func.isRequired,
  focusedInput: PropTypes.func,
  handleTimeChange: PropTypes.func.isRequired,
};

export default DateTimeRangePicker;
